import * as React from 'react'
import { designTokens } from '../lib/design-tokens'
import { cn } from '../lib'
import { CloseIcon } from './icons'

export type PopupModalProps = {
  title?: React.ReactNode
  children?: React.ReactNode
  footer?: React.ReactNode
  variant?: 'desktop' | 'mobile'
  showOverlay?: boolean
  showClose?: boolean
  showCloseText?: boolean
  onClose?: () => void
  closeLabel?: string
  closeTextLabel?: string
  reserveBottomOffset?: boolean
  overlayWidth?: string
  overlayHeight?: string
  panelWidth?: string
  panelHeight?: string
  scale?: number
  className?: string
  overlayClassName?: string
  panelClassName?: string
  allowOverflow?: boolean
  scrollContent?: boolean
}

const PopupModal = ({
  title,
  children,
  footer,
  variant = 'desktop',
  showOverlay = false,
  showClose = true,
  showCloseText = false,
  onClose,
  closeLabel = 'Close dialog',
  closeTextLabel = 'Закрыть',
  reserveBottomOffset = false,
  overlayWidth,
  overlayHeight,
  panelWidth,
  panelHeight,
  scale = 1,
  className,
  overlayClassName,
  panelClassName,
  allowOverflow = false,
  scrollContent,
}: PopupModalProps) => {
  const isMobile = variant === 'mobile'
  const panelPaddingX = isMobile
    ? designTokens.spacing.popupModalMobilePaddingX
    : designTokens.spacing.xxl
  const panelPaddingY = isMobile
    ? designTokens.spacing.popupModalMobilePaddingY
    : designTokens.spacing.xxxl
  const panelRadius = isMobile ? designTokens.borders.radius.xlTop : designTokens.borders.radius.xl
  const panelWidthValue =
    panelWidth ??
    (isMobile ? designTokens.sizes.width.popupModalMobile : designTokens.sizes.width.popupModal)
  const panelHeightValue =
    panelHeight ?? (isMobile ? designTokens.sizes.height.popupModalMobile : 'auto')
  const isDesktopAutoHeight = !isMobile && panelHeightValue === 'auto'
  const gap = designTokens.spacing.popupModalGap
  const overlayWidthValue = overlayWidth ?? designTokens.sizes.width.popupOverlay
  const overlayHeightValue = overlayHeight ?? designTokens.sizes.height.popupOverlay
  const closeSize = designTokens.sizes.width.popupClose
  const closeOffsetX = designTokens.spacing.popupCloseOffsetX
  const closeOffsetY = designTokens.spacing.popupCloseOffsetY
  const fontFamily = designTokens.typography.fontFamilies.primary
  const mobileBottomOffset = reserveBottomOffset
    ? designTokens.spacing.popupModalMobileBottomOffset
    : designTokens.spacing.none
  const shouldScrollContent = scrollContent ?? (isMobile || isDesktopAutoHeight)

  const toPxNumber = (value?: string) => {
    if (!value || !value.endsWith('px')) {
      return undefined
    }
    const numeric = Number.parseFloat(value)
    return Number.isNaN(numeric) ? undefined : numeric
  }

  const normalizeViewportValue = (value?: string) => {
    if (!value) {
      return value
    }
    if (value.endsWith('vw') || value.endsWith('vh')) {
      return '100%'
    }
    return value
  }

  const normalizedOverlayWidth = normalizeViewportValue(overlayWidthValue)
  const normalizedOverlayHeight = normalizeViewportValue(overlayHeightValue)
  const overlayWidthNumber = toPxNumber(overlayWidthValue)
  const overlayHeightNumber = toPxNumber(overlayHeightValue)
  const containerWidth = overlayWidthNumber
    ? `${overlayWidthNumber * scale}px`
    : normalizedOverlayWidth
  const containerHeight = overlayHeightNumber
    ? `${overlayHeightNumber * scale}px`
    : normalizedOverlayHeight
  const mobilePanelMaxHeight = `calc(100dvh - ${mobileBottomOffset})`

  React.useEffect(() => {
    if (!showOverlay || typeof document === 'undefined') {
      return
    }

    const previousBodyOverflow = document.body.style.overflow
    const previousHtmlOverflow = document.documentElement.style.overflow

    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousBodyOverflow
      document.documentElement.style.overflow = previousHtmlOverflow
    }
  }, [showOverlay])

  return (
    <div
      className={cn('relative flex items-center justify-center', className)}
      style={{ width: containerWidth, height: containerHeight }}
    >
      <div
        className="absolute left-0 top-0 overflow-visible"
        style={{
          width: normalizedOverlayWidth,
          height: normalizedOverlayHeight,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {showOverlay ? (
          <div
            className={cn('absolute inset-0', overlayClassName)}
            style={{
              backgroundColor: designTokens.colors.background.overlay,
              borderRadius: designTokens.borders.radius.none,
            }}
          />
        ) : null}
        <div
          className={cn(
            'relative flex h-full w-full justify-center',
            isMobile ? 'items-end' : 'items-center'
          )}
          style={isMobile ? { paddingBottom: mobileBottomOffset } : undefined}
        >
          <div className="relative flex" style={{ width: panelWidthValue }}>
            <div
              className={cn('flex w-full flex-col bg-background', panelClassName)}
              style={{
                width: panelWidthValue,
                height: panelHeightValue,
                maxHeight: isMobile
                  ? mobilePanelMaxHeight
                  : isDesktopAutoHeight
                    ? 'calc(100vh - 48px)'
                    : undefined,
                overflow: allowOverflow ? 'visible' : shouldScrollContent ? 'hidden' : undefined,
                borderRadius: panelRadius,
                backgroundColor: designTokens.colors.background.surface,
                padding: `${panelPaddingY} ${panelPaddingX}`,
                gap,
              }}
            >
              <div
                className="flex w-full flex-1 flex-col"
                style={{
                  gap,
                  minHeight: 0,
                  overflowY: allowOverflow ? 'visible' : shouldScrollContent ? 'auto' : undefined,
                  overscrollBehavior: isMobile ? 'contain' : undefined,
                }}
              >
                {title ? (
                  <div
                    style={{
                      fontFamily,
                      fontSize: designTokens.typography.modal.title.size,
                      lineHeight: designTokens.typography.modal.title.lineHeight,
                      fontWeight: designTokens.typography.modal.title.weight,
                      color: designTokens.colors.text.primary,
                    }}
                  >
                    {title}
                  </div>
                ) : null}
                {children ? (
                  <div
                    style={{
                      fontFamily,
                      fontSize: designTokens.typography.modal.body.size,
                      lineHeight: designTokens.typography.modal.body.lineHeight,
                      fontWeight: designTokens.typography.modal.body.weight,
                      color: designTokens.colors.text.strong,
                    }}
                  >
                    {children}
                  </div>
                ) : null}
              </div>
              {footer || showCloseText ? (
                <div className="flex w-full shrink-0 flex-col" style={{ gap }}>
                  {footer}
                  {showCloseText ? (
                    <button
                      type="button"
                      className="w-full"
                      onClick={onClose}
                      style={{
                        fontFamily,
                        fontSize: designTokens.typography.modal.closeLabel.size,
                        lineHeight: designTokens.typography.modal.closeLabel.lineHeight,
                        fontWeight: designTokens.typography.modal.closeLabel.weight,
                        color: designTokens.colors.text.primary,
                        textAlign: 'center',
                        background: designTokens.colors.background.transparent,
                      }}
                    >
                      {closeTextLabel}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            {showClose && !isMobile ? (
              <button
                type="button"
                aria-label={closeLabel}
                className="absolute flex items-center justify-center"
                style={{
                  width: closeSize,
                  height: closeSize,
                  right: `-${closeOffsetX}`,
                  top: `-${closeOffsetY}`,
                }}
                onClick={onClose}
              >
                <CloseIcon className="h-full w-full text-white" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

PopupModal.displayName = 'PopupModal'

export { PopupModal }
